import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const KebabArt = () => {
  const [activeStep, setActiveStep] = useState(0);

  const preparationSteps = [
    {
      title: "Meat Selection & Preparation",
      duration: "2-4 hours",
      description: `The foundation of exceptional kebab begins with selecting premium cuts of meat. We hand-select each piece, ensuring proper marbling and freshness. The meat is then carefully trimmed and cut against the grain to ensure maximum tenderness. This meticulous preparation sets the stage for the authentic flavors that follow.`,
      image: "https://images.unsplash.com/photo-1588417936104-c4b4112c8fc8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      techniques: [
        "Hand-selection of premium cuts",
        "Proper grain direction cutting",
        "Traditional trimming methods",
        "Temperature-controlled storage"
      ],
      icon: "Scissors"
    },
    {
      title: "Traditional Spice Blending",
      duration: "30 minutes",
      description: `Our signature spice blends are created using recipes passed down through three generations. Each spice is carefully measured and combined in specific sequences to create complex flavor profiles. The blending process involves traditional techniques that ensure even distribution and optimal flavor release during cooking.`,
      image: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      techniques: [
        "Family recipe spice ratios",
        "Sequential blending process",
        "Hand-grinding fresh spices",
        "Quality testing each batch"
      ],
      icon: "Sparkles"
    },
    {
      title: "Marination Process",
      duration: "4-24 hours",
      description: `The marination process is where science meets tradition. Our carefully crafted marinades penetrate deep into the meat fibers, tenderizing while infusing authentic Mediterranean flavors. The timing and temperature are precisely controlled to achieve the perfect balance of tenderness and flavor intensity.`,
      image: "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      techniques: [
        "Temperature-controlled marination",
        "Optimal timing for each cut",
        "Traditional yogurt-based marinades",
        "Regular rotation for even coverage"
      ],
      icon: "Clock"
    },
    {
      title: "Skewer Assembly",
      duration: "15-20 minutes",
      description: `The art of skewer assembly requires skill and experience. Each piece is carefully positioned to ensure even cooking, with consideration for size, thickness, and meat type. The spacing and arrangement follow traditional patterns that have been perfected over generations of kebab masters.`,
      image: "https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      techniques: [
        "Uniform piece sizing",
        "Strategic spacing for even cooking",
        "Traditional skewer patterns",
        "Quality control inspection"
      ],
      icon: "Zap"
    },
    {
      title: "Charcoal Grilling Mastery",
      duration: "8-12 minutes",
      description: `The final and most critical step is the grilling process. Using traditional charcoal methods, our grill masters control temperature, timing, and positioning to achieve the perfect char while maintaining juicy interiors. This ancient cooking method imparts the distinctive smoky flavor that defines authentic kebab.`,
      image: "https://images.unsplash.com/photo-1544025162-d76694265947?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      techniques: [
        "Charcoal temperature control",
        "Traditional turning patterns",
        "Visual and tactile doneness testing",
        "Resting for optimal juiciness"
      ],
      icon: "Flame"
    }
  ];

  const traditionalMethods = [
    {
      title: "Ottoman Empire Techniques",
      description: "Our preparation methods trace back to the royal kitchens of the Ottoman Empire, where kebab was elevated to an art form.",
      icon: "Crown"
    },
    {
      title: "Anatolian Spice Wisdom",
      description: "Traditional spice combinations from the Anatolian region create the complex flavor profiles in our signature blends.",
      icon: "Sparkles"
    },
    {
      title: "Mediterranean Fire Cooking",
      description: "Ancient fire cooking techniques from Mediterranean coastal regions ensure perfect char and smoky flavors.",
      icon: "Flame"
    },
    {
      title: "Nomadic Preservation Methods",
      description: "Time-tested preservation and preparation techniques from nomadic cultures ensure optimal meat quality.",
      icon: "Shield"
    }
  ];

  return (
    <div className="py-8">
      <div className="text-center mb-16">
        <h2 className="font-headline text-4xl md:text-5xl font-bold text-primary mb-6">
          The Art of Authentic Kebab
        </h2>
        <p className="font-body text-lg text-text-secondary max-w-3xl mx-auto">
          Discover the traditional techniques and time-honored methods that transform premium ingredients into the exceptional kebabs that have made us a beloved destination for authentic Mediterranean cuisine.
        </p>
      </div>

      {/* Preparation Process */}
      <div className="mb-16">
        <h3 className="font-headline text-3xl font-bold text-text-primary text-center mb-12">
          Traditional Preparation Process
        </h3>

        {/* Step Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {preparationSteps.map((step, index) => (
            <button
              key={index}
              onClick={() => setActiveStep(index)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-cta font-medium transition-organic ${
                activeStep === index
                  ? 'bg-primary text-white shadow-warm'
                  : 'text-text-primary hover:text-primary hover:bg-primary-50'
              }`}
            >
              <Icon name={step.icon} size={16} />
              <span className="hidden sm:inline">Step {index + 1}</span>
              <span className="sm:hidden">{index + 1}</span>
            </button>
          ))}
        </div>

        {/* Active Step Content */}
        <motion.div
          key={activeStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="card-surface"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <Icon name={preparationSteps[activeStep].icon} size={24} className="text-white" />
                </div>
                <div>
                  <h4 className="font-headline text-2xl font-bold text-text-primary">
                    {preparationSteps[activeStep].title}
                  </h4>
                  <p className="text-accent-600 font-cta font-semibold">
                    Duration: {preparationSteps[activeStep].duration}
                  </p>
                </div>
              </div>
              
              <p className="font-body text-text-secondary leading-relaxed mb-6">
                {preparationSteps[activeStep].description}
              </p>

              <div>
                <h5 className="font-cta font-semibold text-text-primary mb-4 flex items-center space-x-2">
                  <Icon name="CheckCircle" size={18} />
                  <span>Key Techniques</span>
                </h5>
                <ul className="space-y-2">
                  {preparationSteps[activeStep].techniques.map((technique, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-sm text-text-secondary">
                      <Icon name="ArrowRight" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                      <span>{technique}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="relative">
              <div className="relative overflow-hidden rounded-lg shadow-warm-lg">
                <Image
                  src={preparationSteps[activeStep].image}
                  alt={preparationSteps[activeStep].title}
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/40 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3">
                    <p className="font-cta font-semibold text-text-primary text-sm">
                      Step {activeStep + 1} of {preparationSteps.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Traditional Methods */}
      <div className="mb-16">
        <h3 className="font-headline text-3xl font-bold text-text-primary text-center mb-12">
          Ancient Culinary Traditions
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {traditionalMethods.map((method, index) => (
            <motion.div
              key={method.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="card-surface hover:shadow-warm-lg transition-organic"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name={method.icon} size={24} className="text-white" />
                </div>
                <div>
                  <h4 className="font-cta text-lg font-semibold text-text-primary mb-2">
                    {method.title}
                  </h4>
                  <p className="font-body text-text-secondary leading-relaxed">
                    {method.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Difference from Fast Food */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-center"
      >
        <div className="max-w-4xl mx-auto card-surface bg-gradient-to-r from-conversion-50 to-primary-50">
          <Icon name="Award" size={48} className="text-conversion mx-auto mb-6" />
          <h3 className="font-headline text-3xl font-bold text-text-primary mb-6">
            Authentic vs. Fast Food: The Difference
          </h3>
          <p className="font-body text-lg text-text-secondary leading-relaxed mb-8">
            While fast-food kebabs prioritize speed and cost-cutting, our traditional approach focuses on time-honored techniques, premium ingredients, and the patience required to create truly exceptional flavors. Every step in our process is designed to honor the authentic Mediterranean culinary heritage.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-left">
              <h4 className="font-cta text-xl font-semibold text-conversion mb-4 flex items-center space-x-2">
                <Icon name="Star" size={20} />
                <span>Our Authentic Approach</span>
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">Hand-selected premium meat cuts</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">Traditional 4-24 hour marination</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">Charcoal grilling by skilled masters</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">Family recipe spice blends</span>
                </li>
              </ul>
            </div>
            
            <div className="text-left">
              <h4 className="font-cta text-xl font-semibold text-error mb-4 flex items-center space-x-2">
                <Icon name="X" size={20} />
                <span>Fast Food Shortcuts</span>
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <Icon name="X" size={16} className="text-error mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">Pre-processed, lower-grade meat</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="X" size={16} className="text-error mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">Quick 30-minute marination</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="X" size={16} className="text-error mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">Electric or gas grilling for speed</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="X" size={16} className="text-error mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">Mass-produced seasoning mixes</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default KebabArt;